import { ButtonCus, ButtonCusWithIcon } from '@/components/common/Button';
import { Form, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import FormatNumberVer2 from '@/components/react-number-format/ReactNumberFormartver2';
import { DURATION_TIME_TYPES, ekDomain, ROUTES, STATUS_TYPE } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { COMMON_TYPE, FORM_TYPE, FormType } from '@/types/enums';
import { isPathEk } from '@/utils/routes';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

// validate schema
interface Props {
    formType: FormType
}

export default observer(function SettingPriceForm({ formType }: Props) {
    // hook
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const { id } = useParams();
    const nav = useNavigate();

    const isEk = isPathEk(pathname);

    // store
    const {
        capacitieStore: { capacitieList, getCapacitieList, paging },
        priceStore: { createPrice, getPriceDetail, updatePrice },
        modalStore: { showAlertModal }
    } = useStore();

    // validate Schema
    const validateSchema = yup.object().shape({
        name: yup
            .string()
            .required('form_error_validate.required'),
        note: yup
            .string(),
        status: yup
            .string(),
        priceDetails: yup.array().of(
            yup.object().shape({
                id: yup.number().required('form_error_validate.required'),
                values: yup.object().shape(
                    {
                        ...Object.values(DURATION_TIME_TYPES).reduce((acc, time) => {
                            acc[time.value] = yup
                                .string()
                                .checkInputOnlyContainDigitsOrEmpty(t('form_error_validate.only_number'))
                                .test(
                                    'min-value',
                                    t('form_error_validate.min', { min: 0 }),
                                    (val) => {
                                        if (val == null || val === '') {
                                            return true;
                                        }
                                        const num = Number(val);
                                        return num >= 0;
                                    }
                                )
                                .required(t('form_error_validate.required'));
                            return acc;
                        }, {})
                    }
                )
            })
        )

    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            name: '',
            note: '',
            status: STATUS_TYPE.Active.toString(),
            priceDetails: capacitieList.map(row => ({
                id: row.id,
                values: Object.values(DURATION_TIME_TYPES).reduce((acc, col) => {
                    acc[col.value] = 0;
                    return acc;
                }, {})
            }))
        }
    });

    // lifecycle
    useEffect(() => {
        fetchData();
    }, []);

    // function
    const fetchData = async () => {
        const resCapity = await flowResult(getCapacitieList({
            type: isEk ? COMMON_TYPE.EK : COMMON_TYPE.GAS,
            sortKey: 'id',
            sortDir: 'asc'
        }, paging));
        if (resCapity) {
            if (id) {
                const res = await flowResult(getPriceDetail(id));
                if (res) {
                    let priceDetail = resCapity.elements.map(bike => ({
                        id: bike.id,
                        values: Object.values(DURATION_TIME_TYPES).reduce((acc, time) => {
                            acc[time.value] = 0;
                            return acc;
                        }, {})
                    })).map((price) => {
                        let filterPrice = res.priceDetails.filter((item: any) => item.capacitieId === price.id);
                        let clonePrice = { ...price.values };
                        if (filterPrice.length > 0) {
                            clonePrice = filterPrice.reduce((prev, cur) => {
                                prev[cur.time] = cur?.price || 0;

                                return prev;
                            }, {});
                        }

                        return {
                            id: price.id,
                            values: clonePrice
                        };
                    });

                    form.setValue('name', res.name);
                    form.setValue('note', res.note ?? '');
                    form.setValue('status', res.status.toString());
                    form.setValue(
                        'priceDetails',
                        Object.values(priceDetail) as { id: number, values: {} }[]
                    );
                }
            } else {
                form.setValue('priceDetails', resCapity.elements.map(bike => ({
                    id: bike.id,
                    values: Object.values(DURATION_TIME_TYPES).reduce((acc, time) => {
                        acc[time.value] = 0;
                        return acc;
                    }, {})
                })));
            }
        }
    };

    const onSubmit = async (data: FormData) => {
        if (data && data.priceDetails) {
            const formattedData = data?.priceDetails?.flatMap(bikeRow =>
                Object.entries(bikeRow.values).map(([time, price]) => ({
                    capacitieId: bikeRow.id,
                    time: Number(time),
                    price: Number(price)
                })));
            const finalData = {
                name: data.name ?? '',
                note: data.note ?? '',
                status: Number(data.status),
                priceDetails: formattedData

            };

            if (id) {
                const res = await flowResult(updatePrice({ payload: finalData, id: id }));
                if (res) {
                    showAlertModal({
                        id: 'success',
                        type: 'success',
                        content: t('messages.update_success'),
                        onClickXIcon: () => {
                            nav(isEk ? `/${ekDomain}${ROUTES.priceManagement.href}` : ROUTES.priceManagement.href);
                        }
                    });
                }
            } else {
                const res = await flowResult(createPrice(finalData));
                if (res) {
                    showAlertModal({
                        id: 'success',
                        type: 'success',
                        content: t('messages.add_success'),
                        onClickXIcon: () => {
                            nav(isEk ? `/${ekDomain}${ROUTES.priceManagement.href}` : ROUTES.priceManagement.href);
                        }
                    });
                }
            }
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className='layout-card mt-6'>
                    <div className='w-[320px]'>
                        <FormField
                            control={form.control}
                            name='name'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel required>{t('words_title.price_name')}</FormLabel>
                                    <Input
                                        placeholder={t('placeholder.enter')}
                                        type='text'
                                        className='disabled:cursor-default'
                                        disabled
                                        {...field}
                                        value={field.value ?? ''}
                                    />
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className='mt-4'>
                        <div className='grid grid-cols-5 bg-primaryWeb rounded-sm text-white'>
                            <div className='px-7 py-3.5 text-sm font-semibold'>{t('words_title.engine_size')}</div>
                            {Object.values(DURATION_TIME_TYPES).map(time => (
                                <div key={time.key} className='pr-2 py-3.5 flex text-sm font-semibold'>
                                    {t(`${time.key}`)}
                                </div>
                            ))}
                        </div>

                        {
                            capacitieList.map((row, rowIndex) => (
                                <div key={row.id} className='grid grid-cols-5 border-b border-background-2'>
                                    <div className='px-7 flex items-center text-[13px] font-semibold'>
                                        {t(`words_title.${row.code}`)}
                                    </div>
                                    {Object.values(DURATION_TIME_TYPES).map(col => (
                                        <div key={col.value} className='min-h-8 my-2 mr-7'>
                                            <Controller
                                                name={`priceDetails.${rowIndex}.values.${col.value}`}
                                                render={({ field, fieldState }) => (
                                                    <div className='min-h-fit'>
                                                        <FormatNumberVer2
                                                            decimalScale={0}
                                                            prefix={t('currency.currency_symbol')}
                                                            statusDisplay='input'
                                                            thousandSeparator={true}
                                                            allowNegative={false}
                                                            className='h-10 border rounded py-1 px-2 disabled:cursor-default'
                                                            value={field.value}
                                                            setvalueOfNumericFormat={(values) => {
                                                                field.onChange(values);
                                                            }}
                                                        />
                                                        <div>{fieldState.invalid && <p className='text-[0.8125rem] font-normal text-destructive whitespace-pre-line pl-[0.0625rem] mt-[0.125rem]'>{fieldState?.error?.message}</p>}</div>
                                                    </div>

                                                )}
                                            />
                                        </div>
                                    ))}
                                </div>
                            ))
                        }
                    </div>
                </div>

                <div className='mt-6 flex items-center justify-center gap-4'>
                    {formType === FORM_TYPE.Create ?
                        (
                            <ButtonCus variant='default' size='sm' type='submit'>
                                {t('button.add')}
                            </ButtonCus>
                        ) :
                        (
                            <ButtonCusWithIcon
                                type='submit'
                                variant='default'
                                size='sm'
                                iconId='#icon-update'
                            >
                                {t('button.update')}
                            </ButtonCusWithIcon>
                        )}
                </div>

            </form>
        </Form>
    );
});
